import React, { useEffect, useState } from 'react';
import { Loader2, Unlink } from 'lucide-react';
import CommonModal from '../../ui/CommonModal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { Avatar } from '../../ui/Avatar';
import { User } from '../../../types';
import { useCloseAndReleasePlayer } from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';

const CLOSE_CONFIRM_WORD = 'close';

interface CloseReleasePlayerModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function playerPrimaryLine(u: User) {
  return u.phone?.trim() || u.email?.trim() || u.username?.trim() || '—';
}

const CloseReleasePlayerModal: React.FC<CloseReleasePlayerModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [executeError, setExecuteError] = useState<string | null>(null);
  const closeMutation = useCloseAndReleasePlayer();

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setConfirmText('');
      setExecuteError(null);
    }
  }, [isOpen]);

  if (!isOpen || !user) return null;

  const confirmPhraseOk = confirmText.trim().toLowerCase() === CLOSE_CONFIRM_WORD;
  const reasonOk = reason.trim().length > 0;
  const canSubmit = reasonOk && confirmPhraseOk && !closeMutation.isLoading;
  const isBusy = closeMutation.isLoading;

  const avatarUser = {
    id: user.id,
    name: user.name ?? undefined,
    userName: user.userName ?? undefined,
    username: user.username ?? undefined,
    email: user.email ?? undefined,
    profile: user.profile,
  };

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !user.id) return;
    setExecuteError(null);
    try {
      const res = await closeMutation.mutateAsync({ userId: user.id, reason: reason.trim() });
      toast.success(res.message || 'Player closed. Contacts released.');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; code?: string } } };
      const msg = ax.response?.data?.message || 'Close & release failed. Account was not changed.';
      setExecuteError(msg);
    }
  };

  const title = (
    <span className="flex items-center gap-2 text-destructive">
      <Unlink className="h-5 w-5 shrink-0" aria-hidden />
      Close & release contacts
    </span>
  );

  return (
    <CommonModal isOpen={isOpen} onClose={handleClose} size="lg" closeDisabled={isBusy} title={title}>
      <div className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
          <Avatar user={avatarUser} size="md" deterministicColor className="shrink-0" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate font-semibold">{playerPrimaryLine(user)}</p>
            <p className="font-mono text-[11px] text-muted-foreground truncate" title={user.id}>
              ID: {user.id}
            </p>
          </div>
        </div>

        <div
          className="rounded-lg border border-destructive/40 bg-red-50 p-3 text-sm text-red-950"
          role="alert"
        >
          <p className="font-semibold">Permanent close. Cannot be undone.</p>
          <p className="mt-2">
            The account is closed. Email, phone, username, and Telegram are rewritten so the same
            contacts can register a new player. In-game and wallet balances stay on this closed
            account and are not recalled.
          </p>
        </div>

        {executeError ? (
          <div className="rounded-md border border-destructive/50 bg-red-50 p-3 text-sm text-red-900" role="alert">
            <p className="font-semibold">{executeError}</p>
          </div>
        ) : null}

        <div className="space-y-3">
          <div className="space-y-2">
            <label htmlFor="close-release-reason" className="text-sm font-medium">
              Reason <span className="text-red-500">*</span>
            </label>
            <Input
              id="close-release-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for closing this player…"
              disabled={isBusy}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="close-release-confirm" className="text-sm font-medium">
              Type <span className="font-mono">{CLOSE_CONFIRM_WORD}</span> to confirm{' '}
              <span className="text-red-500">*</span>
            </label>
            <Input
              id="close-release-confirm"
              autoComplete="off"
              className="font-mono"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CLOSE_CONFIRM_WORD}
              disabled={isBusy}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isBusy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Closing…
              </span>
            ) : (
              'Close & release'
            )}
          </Button>
        </div>
      </div>
    </CommonModal>
  );
};

export default CloseReleasePlayerModal;
